import {
  type ChartworkSymbol as Symbol,
  type TrackColour,
} from "@nav-games/chartwork";

interface ChartworkSymbolProps {
  symbol: Symbol;
  size?: number;
}

const STROKE_HEX: Record<TrackColour, string> = {
  orange: "#e08020",
  magenta: "#d000a0",
  green: "#3aa055",
  black: "#1c2530",
};

/// Renders a chartwork symbol on a 100×100 viewbox. Each `kind`
/// draws a different glyph centred on the box; the renderer in the
/// catalogue + flashcards just embeds this at whatever pixel size
/// they want.
export function ChartworkSymbol({ symbol, size = 80 }: ChartworkSymbolProps) {
  const stroke = STROKE_HEX[symbol.colour];
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ display: "block" }}
      aria-label={symbol.name}
    >
      {symbol.kind === "fix" && (
        <>
          <line x1="50" y1="20" x2="50" y2="80" stroke={stroke} strokeWidth="2" />
          <line x1="20" y1="50" x2="80" y2="50" stroke={stroke} strokeWidth="2" />
          <circle cx="50" cy="50" r="14" fill="none" stroke={stroke} strokeWidth="3" />
          <circle cx="50" cy="50" r="3" fill={stroke} />
        </>
      )}
      {symbol.kind === "dr_tick" && (
        <>
          {/* Water-track line — slope -1/4 from (10,60) to (90,40). */}
          <line x1="10" y1="60" x2="90" y2="40" stroke={stroke} strokeWidth="3" />
          {/* DR tick — PERPENDICULAR to the track at its midpoint
              (50, 50). Slope of perpendicular = 4 (negative
              reciprocal of -1/4). Tick spans ±8 along that
              perpendicular so it visibly crosses the track. */}
          <line
            x1="46"
            y1="34"
            x2="54"
            y2="66"
            stroke={stroke}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </>
      )}
      {symbol.kind === "ep_triangle" && (
        <>
          <polygon
            points="50,28 72,68 28,68"
            fill="none"
            stroke={stroke}
            strokeWidth="3"
          />
          <circle cx="50" cy="58" r="3" fill={stroke} />
        </>
      )}
      {symbol.kind === "waypoint" && (
        <>
          <rect
            x="28"
            y="28"
            width="44"
            height="44"
            fill="none"
            stroke={stroke}
            strokeWidth="3"
          />
          <circle cx="50" cy="50" r="3" fill={stroke} />
        </>
      )}
      {symbol.kind === "chevron" &&
        Array.from({ length: symbol.chevronCount ?? 1 }).map((_, i) => {
          const x = 35 + i * 14;
          return (
            <polyline
              key={i}
              points={`${x},32 ${x + 12},50 ${x},68`}
              fill="none"
              stroke={stroke}
              strokeWidth="4"
              strokeLinejoin="round"
            />
          );
        })}
    </svg>
  );
}
